import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

interface DropdownItem {
  name: string;
  href: string;
}

interface NavItem {
  name: string;
  href?: string;
  children?: DropdownItem[];
}

const Header: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigation: NavItem[] = [
    { name: '홈', href: '/' },
    {
      name: '센터 정보',
      children: [
        { name: '우리의 철학', href: '/philosophy' },
        { name: '센터 소개', href: '/about' },
        { name: '교수진', href: '/faculty' },
        { name: '운영세칙', href: '/governance' },
      ],
    },
    { name: '인간-AI 협력 교육헌장', href: '/charter' },
    {
      name: '연구·활동',
      children: [
        { name: '연구 분과', href: '/divisions' },
        { name: '인간-AI협력 컬럼', href: '/column' },
        { name: '행사 (AI4HER)', href: '/ai4her' },
      ],
    },
    { name: '새소식', href: '/news' },
  ];

  const isActive = (href: string) =>
    href === '/'
      ? location.pathname === '/' || location.pathname === '/home'
      : location.pathname === href;

  const isGroupActive = (children: DropdownItem[]) =>
    children.some((c) => location.pathname === c.href);

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'glass shadow-lg shadow-navy-900/5'
          : 'glass'
      } ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group">
            <img
              src="/chair-logo.png"
              alt="CHAIR"
              className="h-9 w-auto object-contain mr-2.5 transition-transform duration-300 group-hover:scale-105"
            />
            <div>
              <span className="text-sm sm:text-base font-bold text-navy-900 tracking-tight leading-none">
                CHAIR
              </span>
              <span className="block text-[9px] sm:text-[10px] text-navy-500 font-medium tracking-wide">
                Center for Human-AI Research
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-0.5" ref={dropdownRef}>
            {navigation.map((item) => {
              if (item.href) {
                const isCharter = item.href === '/charter';
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3.5 py-2 rounded-lg text-[13px] font-semibold tracking-tight transition-all duration-200 ${
                      isCharter
                        ? isActive(item.href)
                          ? 'text-white bg-teal-500 shadow-sm shadow-teal-500/25'
                          : 'text-teal-600 bg-teal-50 hover:bg-teal-500 hover:text-white hover:shadow-sm'
                        : isActive(item.href)
                          ? 'text-navy-900 bg-navy-50'
                          : 'text-navy-600 hover:text-navy-900 hover:bg-navy-50/60'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              const isOpen = openDropdown === item.name;
              const groupActive = item.children ? isGroupActive(item.children) : false;
              return (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => setOpenDropdown(isOpen ? null : item.name)}
                    className={`flex items-center px-3.5 py-2 rounded-lg text-[13px] font-semibold tracking-tight transition-all duration-200 ${
                      groupActive || isOpen
                        ? 'text-navy-900 bg-navy-50'
                        : 'text-navy-600 hover:text-navy-900 hover:bg-navy-50/60'
                    }`}
                  >
                    {item.name}
                    <ChevronDown
                      className={`ml-1 h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isOpen && item.children && (
                    <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl shadow-navy-900/10 border border-navy-100/50 py-2 z-50 animate-fade-in">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setOpenDropdown(null)}
                          className={`block px-4 py-2.5 text-[13px] transition-all duration-150 ${
                            location.pathname === child.href
                              ? 'text-teal-600 bg-teal-50/60 font-semibold'
                              : 'text-navy-600 hover:text-navy-900 hover:bg-navy-50/40 hover:pl-5'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-navy-700 hover:text-teal-600 hover:bg-navy-50 transition-colors"
            aria-label="메뉴 열기"
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-navy-100/50 pb-4 pt-2 animate-fade-in">
            {navigation.map((item) => {
              if (item.href) {
                const isCharter = item.href === '/charter';
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-4 py-2.5 text-sm font-medium rounded-lg mx-2 my-0.5 transition-colors ${
                      isCharter
                        ? isActive(item.href)
                          ? 'text-white bg-teal-500'
                          : 'text-teal-600 bg-teal-50 hover:bg-teal-500 hover:text-white'
                        : isActive(item.href)
                          ? 'text-navy-900 bg-navy-50'
                          : 'text-navy-600 hover:text-navy-900 hover:bg-navy-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              const isOpen = openMobileDropdown === item.name;
              const groupActive = item.children ? isGroupActive(item.children) : false;
              return (
                <div key={item.name}>
                  <button
                    onClick={() => setOpenMobileDropdown(isOpen ? null : item.name)}
                    className={`flex items-center justify-between w-full px-4 py-2.5 text-sm font-medium rounded-lg mx-2 my-0.5 transition-colors ${
                      groupActive || isOpen
                        ? 'text-navy-900 bg-navy-50'
                        : 'text-navy-600 hover:text-navy-900 hover:bg-navy-50'
                    }`}
                    style={{ width: 'calc(100% - 16px)' }}
                  >
                    {item.name}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && item.children && (
                    <div className="ml-6 border-l-2 border-teal-200 pl-3 my-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => { setIsMenuOpen(false); setOpenMobileDropdown(null); }}
                          className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                            location.pathname === child.href
                              ? 'text-teal-600 bg-teal-50 font-medium'
                              : 'text-navy-500 hover:text-navy-900 hover:bg-navy-50'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
